import { inject, Injectable } from '@angular/core';
import { loadScript } from '../lib/load-script';
import { DataLoaderService } from './data-loader.service';

export const GOOGLE_API_KEY = 'AIzaSyA3QtG-HpoiklX8jid6_AriI9ToWlEbzfg';
export const GOOGLE_CLIENT_ID = '387343500342-obn0crc436d0kubai59g2frmat1sbmvl.apps.googleusercontent.com';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gapi: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleApiService {
  private loader = inject(DataLoaderService);

  private cachedTokenInfoMap = new Map<string, { accessToken: string; expiresAt: number }>();

  private async loadGoogleClient(scope: string, clientName: string) {
    return this.loader.get('google-drive-client' + scope, async () => {
      await loadScript('https://apis.google.com/js/api.js');
      await loadScript('https://accounts.google.com/gsi/client');
      await this.loadGapi(clientName);

      return window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope,
      });
    });
  }

  public async requestAccess(scope: string, clientName: string): Promise<string> {
    const tokenClient = await this.loadGoogleClient(scope, clientName);

    const cachedToken = this.cachedTokenInfoMap.get(scope);
    if (cachedToken && cachedToken.expiresAt > Date.now() + 60000) {
      return cachedToken.accessToken;
    }

    const getToken = (prompt: '' | 'consent') => {
      return new Promise<string>((resolve, reject) => {
        tokenClient.callback = resp => {
          if (resp.error) return reject(resp);

          const accessToken: string = resp.access_token;
          const expiresIn: number = Number(resp.expires_in ?? 3600);

          this.cachedTokenInfoMap.set(scope, {
            accessToken,
            expiresAt: Date.now() + Math.max(0, expiresIn) * 1000,
          });

          resolve(accessToken);
        };

        tokenClient.requestAccessToken({ prompt }); // silent if '', interactive if 'consent'
      });
    };

    // 3) Try silent first; if it fails because interaction is required, fall back to consent
    try {
      return await getToken('');
    } catch (e) {
      // Typical errors to treat as "needs interaction": access_denied, user_interaction_required, consent_required
      if (e?.error === 'access_denied' || e?.error === 'consent_required' || e?.error === 'user_interaction_required') {
        return await getToken('consent');
      }
      throw e; // real error
    }
  }

  private async loadGapi(clientName: string) {
    return new Promise<void>(resolve => {
      window.gapi.load(clientName, resolve);
    });
  }
}
